import { useCallback, useEffect, useState } from 'react';
import type { MapResponse, MapSpot } from '@kratispot/shared';
import { ApiError, fetchVenueMap } from '@/lib/api';

export type MapState =
  | { phase: 'loading' }
  | { phase: 'ready'; map: MapResponse }
  | { phase: 'no-layout' }
  | { phase: 'failed' };

export type VenueMap = {
  readonly state: MapState;
  readonly reload: () => void;
  readonly apply: (spots: readonly MapSpot[]) => void;
};

// The server owns every spot it sends back; applying its answer is never a merge.
export function replaceSpots(map: MapResponse, spots: readonly MapSpot[]): MapResponse {
  const changed = new Map(spots.map((spot) => [spot.id, spot]));
  return { ...map, spots: map.spots.map((spot) => changed.get(spot.id) ?? spot) };
}

export function useVenueMap(venueId: string, date: string | null): VenueMap {
  const [state, setState] = useState<MapState>({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    fetchVenueMap(venueId, date, controller.signal)
      .then((map) => setState({ phase: 'ready', map }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const missing = error instanceof ApiError && error.status === 404;
        setState({ phase: missing ? 'no-layout' : 'failed' });
      });
    return () => controller.abort();
  }, [venueId, date, attempt]);

  const apply = useCallback((spots: readonly MapSpot[]) => {
    setState((current) =>
      current.phase === 'ready' ? { ...current, map: replaceSpots(current.map, spots) } : current,
    );
  }, []);

  return { state, reload: useCallback(() => setAttempt((n) => n + 1), []), apply };
}
